<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import BostonBlizzard from './images/boston_winter.jpg';
	import FrozenPipe from './images/frozen_pipe.jpg';
	import GuyColdOnCouchVintage from './images/guy_cold_on_couch_vintage.png';
	import StormSky from './images/stormy_skys.jpg';
	import WomanNextToRadiator from './images/woman_next_to_radiator.jpg';
</script>

<Section>
	<p>
		For many of us, the term “polar vortex” first came onto our radar in 2019.
		It caused extremely cold temperatures, mainly in Midwestern states, which
		resulted in dozens of deaths. In this post, we will explain why you should
		expect to hear “polar vortex” more in the future, despite a warming globe,
		and how to prepare for it.
	</p>
	<Image
		alt="Cold family of 4 crowded for warmth next to window. Polar vortex forecasts allow time to prepare."
		src={GuyColdOnCouchVintage}
		class="w-full"
	/>
</Section>

<Section>
	<h2>What is a Polar Vortex?</h2>
	<p>
		Before we explain how to prepare for the polar vortex, let’s first discuss
		what it is. Here are a few polar vortex facts from <a
			href="https://www.weather.gov/safety/cold-polar-vortex"
			target="_blank"
			rel="external">National Weather Service</a
		>:
	</p>
	<Bubble>
		<p class="mb-8 text-lg font-bold text-indow-grey!">Polar Vortext Facts</p>
		<ul>
			<li>Polar vortices have always been over Earth’s poles</li>
			<li>The jet stream keeps the polar vortex in place</li>
			<li>
				Temperatures drop in specific regions as opposed to entire northern
				hemisphere
			</li>
			<li>
				Warming poles due to climate change mean more frequent polar vortices
			</li>
			<li>Many Americans are not prepared for polar vortex conditions today</li>
		</ul>
	</Bubble>
	<p>
		The polar vortex is a large area of cold air located above the Earth’s
		poles. The jet stream, which is a band of quickly moving air at the edge of
		the polar vortex, keeps the polar vortex from coming far south often. The
		temperature difference between the poles and the middle areas of the globe
		are what keeps the jet stream moving quickly.
	</p>
	<p>
		When temperatures between the poles and the middle regions approach one
		another, the jet stream slows down and begins to make a wavy pattern. This
		is different from its regular smooth, circular line. This wavy pattern
		creates high peaks and deep valleys, which is what allows the frigid Arctic
		air to sometimes come south well into the US.
	</p>
	<Image
		src={BostonBlizzard}
		alt="Snow-covered Boston street: Polar vortices are harder to prepare for in warmer areas. Start now."
		class="w-full"
	/>
</Section>

<Section>
	<h2>Polar Vortex Forecast</h2>
	<p>
		<a
			href="https://www.nationalgeographic.com/environment/article/polar-vortex"
			target="_blank"
			rel="external">National Geographic</a
		> explains that with the Earth’s poles warming more quickly than its middle regions,
		we should expect to see temperatures on either side of the jet stream approach
		one another more often. In turn, we should expect to see the polar vortex dipping
		south more often. So while a warming earth means milder winters, and less extreme
		temperatures in the polar vortices, those temperatures are still plenty cold enough
		to cause serious harm to property and living beings.
	</p>
	<p>
		Once the jet stream is weakened and cold air is able to move further south,
		it can take weeks for that air to reach parts of the US, so there will be
		time to prepare for the next one.
	</p>
	<Image
		alt="Stormy, gray sky in winter. Prepare for polar vortices when forecast says it’s on the way."
		src={StormSky}
		class="w-full"
	/>
</Section>

<Section>
	<h2>How to Prepare Your Home & Family for the Polar Vortex</h2>
	<p>
		It’s important to prepare for the next polar vortex ahead of time. Make a
		plan now and keep prepared during cold months. We all know how grocery
		stores are a pain when news of an impending storm breaks. Here are a few
		things to keep in mind to make sure you’re prepared for the polar vortex:
	</p>
	<p>
		<strong>1. Stay Warm</strong> – Physical safety is most important. Make sure you
		have plenty of clothing and blankets in case extreme temperatures caused by the
		polar vortex affect the power grid and your home.
	</p>
	<p>
		There are ways to insulate your home to prepare for power outages. Indow
		inserts add a second layer in your window frames to help keep warm air
		inside and stop cold outside air from penetrating during winter.
	</p>
	<Image
		alt="Frozen busted pipe. Consider how to keep your home’s pipes safe when preparing for the polar vortex."
		src={FrozenPipe}
		class="w-full"
	/>
	<p>
		<strong>2. Maintain Good Hygiene</strong> – If there is a power outage, you might
		be left without running water. Make sure you are well stocked on water for drinking,
		washing, and flushing. Clean hands are important for preventing illness. Have
		several gallons of water for each person in your home. In serious storms that
		can take power out for an extended period of time, consider filling your bathtub
		with water and using that when needed. Any water stored in the bathtub during
		a storm should always be boiled before consuming.
	</p>
	<p>
		<strong>3. Stock Up on Food</strong> – Make sure you have plenty of healthy, nonperishable
		food. In case there is a power outage, have plenty of food that does not need
		to be cooked before eating. Even without a power outage, restaurants and stores
		might be closed. Non-military MREs (meals ready to eat) are sustaining and can
		be found at large stores like Walmart, Costco, and Sam’s Club.
	</p>
	<p>
		<strong>4. Keep the Outside of Your Home Clear</strong> – As much as possible,
		keep your sidewalks, driveway, and car clear of snow and ice. In case of emergency,
		you will want to have quick, easy access to your car. Be sure to run it every
		once in a while. Road salt is good for your driveways, and you can find it at
		most stores.
	</p>
	<p>
		In extreme temperatures, which are seen during a polar vortex, please avoid
		staying outside for longer than a couple minutes. Even a few minutes in
		extremely cold temperatures can be dangerous to your health.
	</p>
	<p>
		<strong>5. Watch Out for Gas & Portable Heaters Indoors</strong> – These can be
		lifesaving items during a major winter storm, but it is important that they never
		be left running unattended, or for too long. Turn your heaters off and unplug
		them if you leave the room or are going to bed. Designate two people in your home
		to always check that gas stoves and portable heaters are turned off when not in
		use.
	</p>
	<Image
		src={WomanNextToRadiator}
		alt="Woman huddled next to the radiator with hot tea. Prepare for power outages during the polar vortex."
		class="w-full"
	/>
</Section>

<Section>
	<h2>Be a Good Citizen & Neighbor</h2>
	<p>
		We caution you to prepare your home for extreme weather events because
		exposure to the elements can cause serious health problems and even death.
		Once your home and family are prepared for the polar vortex, prepare to help
		others in your community.
	</p>
	<p>
		Donating extra clothing items and blankets to shelters is a great way to
		help those in need stay warm. If you live in Portland, Oregon, refer to this <a
			href="https://www.portlandoregon.gov/fish/article/375712"
			target="_blank"
			rel="external">list of donation sites</a
		>. Searching “donate clothes and blankets + your city” in Google will allow
		you to find similar resources.
	</p>
	<p>
		If you see someone outside in extreme weather events, dialing <a
			href="https://www.211info.org/"
			target="_blank"
			rel="external">211</a
		> to get connected with people that can help might save a life.
	</p>
</Section>

<Reviews />
